import { BillingRecord, Invoice } from '@/renderer/types/billing';

export const formatMoney = (
  amount: number,
  options?: {
    currency?: string;
    fromMinorUnits?: boolean;
    maximumFractionDigits?: number;
  },
) => {
  const value = options?.fromMinorUnits ? amount / 100 : amount;

  return new Intl.NumberFormat('en-US', {
    currency: options?.currency || 'USD',
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    minimumFractionDigits: 0,
    style: 'currency',
  }).format(value);
};

export const formatBillingDate = (
  value?: string | null,
  options?: Intl.DateTimeFormatOptions,
) => {
  if (!value) return 'Not available';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...options,
  });
};

export const getInvoiceStatusTone = (status: Invoice['status']) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'open':
      return 'warning';
    case 'void':
    case 'uncollectible':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const getSubscriptionTone = (subscription?: BillingRecord | null) => {
  if (subscription?.suspended_at) {
    return 'danger';
  }

  if (subscription?.cancel_at_period_end) {
    return 'warning';
  }

  switch (subscription?.subscription_status) {
    case 'active':
      return 'success';
    case 'trialing':
      return 'warning';
    case 'past_due':
    case 'incomplete':
      return 'danger';
    case 'canceled':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const getSubscriptionLabel = (subscription?: BillingRecord | null) => {
  if (!subscription) return 'No active subscription';
  if (subscription.suspended_at) return 'Billing recovery';
  if (subscription.cancel_at_period_end) return 'Scheduled to cancel';

  switch (subscription.subscription_status) {
    case 'active':
      return 'Active plan';
    case 'trialing':
      return 'Free trial';
    case 'past_due':
      return 'Payment issue';
    case 'incomplete':
      return 'Setup incomplete';
    case 'canceled':
      return 'Canceled';
    default:
      return subscription.subscription_status;
  }
};

export const getInvoiceRowLabel = (invoice: Invoice) => {
  if (invoice.period_start && invoice.period_end) {
    return `${formatBillingDate(invoice.period_start, {
      month: 'short',
      year: 'numeric',
    })} to ${formatBillingDate(invoice.period_end, {
      month: 'short',
      year: 'numeric',
    })}`;
  }

  return formatBillingDate(invoice.invoice_date, {
    month: 'short',
    year: 'numeric',
  });
};
